import { EmulatedStorage } from './emulated-storage';

/** Browser storage type */
type StorageType = 'localStorage' | 'sessionStorage';

/**
 * Uses native localStorage or sessionStorage if available.
 * Otherwise in-memory storage is used.
 * If in-memory object already exists in window, that one is used. Otherwise new one is created.
 */
export class AppStorage implements Storage {
  private readonly storageType: StorageType;
  private _underlyingStorage: Storage;

  constructor(storageType: StorageType) {
    this.storageType = storageType;
    this._underlyingStorage = this.getStorage();
  }

  private getStorage(): Storage {
    let storage: any;

    try {
      /** No window when executing in node */
      /* istanbul ignore next */
      storage =
        typeof window === 'undefined'
          ? (global as any)[this.storageType]
          : window[this.storageType as keyof Window];

      const x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return storage;
    } catch (e: any) {
      if (
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage &&
        storage.length !== 0
      ) {
        throw e;
      } else {
        /** No window when executing in node */
        /* istanbul ignore next */
        const parent = typeof window === 'undefined' ? global : window;
        const storageProperty = `emulated${this.storageType}`;
        if (!parent.hasOwnProperty(storageProperty)) {
          (parent as any)[storageProperty] = new EmulatedStorage();
        }
        return (parent as any)[storageProperty];
      }
    }
  }

  /** Get underlying native or emulated storage. Used in tests. */
  public get underlyingStorage(): Storage {
    return this._underlyingStorage;
  }

  /** Reload the storage. Used in tests. */
  public reload(): void {
    this._underlyingStorage = this.getStorage();
  }

  public get length(): number {
    return this._underlyingStorage.length;
  }

  public getItem(key: string): string | null {
    return this._underlyingStorage.getItem(key);
  }

  public setItem(key: string, value: string): void {
    return this._underlyingStorage.setItem(key, value);
  }

  public removeItem(key: string): void {
    return this._underlyingStorage.removeItem(key);
  }

  public clear(): void {
    return this._underlyingStorage.clear();
  }

  public key(index: number): string | null {
    return this._underlyingStorage.key(index);
  }
}

export const LocalStorage = new AppStorage('localStorage');
export const SessionStorage = new AppStorage('sessionStorage');
